import { useState, useRef } from "react";
import { useSignal } from "@preact/signals-react";
import { toast } from "react-toastify";
import { Auth } from "aws-amplify";
import TextEdit from "../../../../molecules/TextEdit";
import SelectTextField from "../../../../molecules/SelectTextField";
import CurrencyInput from "../../../../molecules/CurrencyInput";
import { GuarantorItem, GuarantorsData } from "../../types/operations";
import appConfig from "../../../../../config/app-config";

interface MainRiskOption {
    value: string;
    label: string;
}

interface Props {
    guarantors: GuarantorsData;
    onAdd: (guarantor: GuarantorItem) => void;
    onRemove: (index: number) => void;
    guaranteeValue: number | null;
    onGuaranteeValueChange: (value: number | null) => void;
    disabled?: boolean;
    mainRiskValue?: string;
    mainRiskOptions?: MainRiskOption[];
    onMainRiskChange?: (value: string) => void;
    mainRiskError?: string;
}

const { address: PERSON_SERVICE_URL, key: API_KEY_PERSON } = appConfig.apis.personApiEnv;

const GuarantorsManager: React.FC<Props> = ({
    guarantors,
    onAdd,
    onRemove,
    guaranteeValue,
    onGuaranteeValueChange,
    disabled = false,
    mainRiskValue,
    mainRiskOptions,
    onMainRiskChange,
    mainRiskError
}) => {
    const [guarantorSearch, setGuarantorSearch] = useState('');
    const [guarantorOptions, setGuarantorOptions] = useState<Array<{ value: string; label: string; document: string; entity_type: string }>>([]);
    const [isSearching, setIsSearching] = useState(false);
    const debounceTimer = useRef<NodeJS.Timeout | null>(null);
    const abortController = useRef<AbortController | null>(null);

    const newGuarantor = useSignal<{ uuid: string; name: string; document: string; entity_type: string }>({
        uuid: '',
        name: '',
        document: '',
        entity_type: ''
    });

    const handleSearch = (searchValue: string) => {
        setGuarantorSearch(searchValue);

        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        if (abortController.current) {
            abortController.current.abort();
        }

        if (searchValue.length >= 3) {
            setIsSearching(true);
            debounceTimer.current = setTimeout(async () => {
                abortController.current = new AbortController();

                try {
                    const token = await Auth.currentSession()
                        .then((user) => user.getIdToken().getJwtToken());

                    const response = await fetch(`${PERSON_SERVICE_URL}/person/ALL/${searchValue}`, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'x-api-key': API_KEY_PERSON || ''
                        },
                        signal: abortController.current.signal
                    });

                    if (response.ok) {
                        const data = await response.json();
                        const items = data.results.map((item: any) => {
                            const cnpjDoc = item.documents?.find((doc: any) => doc.type === 'CNPJ');
                            const cpfDoc = item.documents?.find((doc: any) => doc.type === 'CPF');
                            const document = cnpjDoc?.number || cpfDoc?.number || '';
                            return {
                                value: item.uuid,
                                label: `${item.name} - ${document}`,
                                document: document,
                                entity_type: item.personType === 'LEGAL_PERSON' ? 'PJ' : 'PF'
                            };
                        });
                        setGuarantorOptions(items);

                        if (items.length > 0) {
                            newGuarantor.value = {
                                uuid: items[0].value,
                                name: items[0].label.split(' - ')[0],
                                document: items[0].document,
                                entity_type: items[0].entity_type
                            };
                        }
                    }
                } catch (error: any) {
                    if (error.name !== 'AbortError') {
                        toast.error('Erro ao buscar avalistas');
                    }
                } finally {
                    setIsSearching(false);
                }
            }, 850);
        } else {
            setIsSearching(false);
        }
    };

    const handleAdd = () => {
        if (!newGuarantor.value.uuid) {
            toast.error("Selecione um avalista");
            return;
        }

        const selected = guarantorOptions.find(g => g.value === newGuarantor.value.uuid);
        if (!selected) return;

        if (!selected.document || selected.document.trim() === '') {
            toast.error("Avalista sem documento cadastrado");
            return;
        }

        onAdd({
            uuid: newGuarantor.value.uuid,
            name: selected.label.split(' - ')[0],
            document: selected.document,
            entity_type: selected.entity_type
        });

        newGuarantor.value = {
            uuid: '',
            name: '',
            document: '',
            entity_type: ''
        };
    };

    return (
        <div>
            <h3 className="text-md font-semibold mt-4 mb-3 text-gray-700 dark:text-gray-200">Avalistas</h3>

            <div className="grid grid-cols-12 gap-4">
                <div className="col-span-3">
                    <CurrencyInput
                        id="guarantee_percentage"
                        label="% aval da operação"
                        prefix="%"
                        fractionDigits={2}
                        value={guaranteeValue || 0}
                        onChange={(value) => onGuaranteeValueChange(value)}
                    />
                </div>
            </div>

            <div className="grid grid-cols-12 gap-4 p-4 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600 mt-4">
                <div className="col-span-2">
                    <TextEdit
                        label="Buscar Avalista"
                        value={guarantorSearch}
                        onChange={(e) => handleSearch(e.target.value)}
                    />
                    {isSearching && (
                        <div className="text-sm text-blue-600 mt-1">
                            Buscando avalistas...
                        </div>
                    )}
                </div>
                <div className="col-span-8">
                    <SelectTextField
                        id="guarantor_uuid"
                        label="Avalista"
                        value={newGuarantor.value.uuid}
                        items={guarantorOptions}
                        onChange={(e) => {
                            const selected = guarantorOptions.find(g => g.value === e.target.value);
                            newGuarantor.value = {
                                uuid: e.target.value,
                                name: selected?.label.split(' - ')[0] || '',
                                document: selected?.document || '',
                                entity_type: selected?.entity_type || ''
                            };
                        }}
                    />
                </div>
                <div className="col-span-2 flex items-end">
                    <button type="button"
                        onClick={handleAdd}
                        className="btn-add mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={disabled}
                    >
                        Incluir
                    </button>
                </div>
            </div>

            <div className="mt-4 overflow-x-auto">
                <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Nome</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Documento</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Tipo</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                        {guarantors.items.length === 0 ? (
                            <tr>
                                <td colSpan={4} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                                    Nenhum avalista adicionado
                                </td>
                            </tr>
                        ) : (
                            guarantors.items.map((guarantor, index) => (
                                <tr key={`${index}-${guarantor.uuid}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100 font-medium">
                                        {guarantor.name}
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100">
                                        {guarantor.document}
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap">
                                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${guarantor.entity_type === 'PJ'
                                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100'
                                            : 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                                            }`}>
                                            {guarantor.entity_type === 'PJ' ? 'Pessoa Jurídica' : 'Pessoa Física'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap">
                                        {guarantor.participant_type_name === 'GUARANTOR-RETAIL' ? (
                                            <span className="text-gray-500 text-xs italic">
                                                Não removível
                                            </span>
                                        ) : (
                                            <button type="button"
                                                onClick={() => onRemove(index)}
                                                className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                disabled={disabled}
                                            >
                                                Remover
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {mainRiskOptions && onMainRiskChange && (
                <div className="grid grid-cols-12 gap-4 mt-4">
                    <div className="col-span-6">
                        <SelectTextField
                            id="main_risk_uuid"
                            label="Risco Principal"
                            value={mainRiskValue || ''}
                            items={mainRiskOptions}
                            onChange={(e) => onMainRiskChange(e.target.value)}
                            errorMessage={mainRiskError}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default GuarantorsManager;
